import axios from "axios";

import {
  destinationCardUsageReadiness,
  provisionDestinationCard,
} from "../services/api";
import type { DestinationPayoutInput } from "../interfaces/payout-instrument";
import { registerPayoutCard } from "./register-payout-card";

export type PreparePayoutRailStatus = "ready" | "failed";

export interface PreparePayoutRailResult {
  status: PreparePayoutRailStatus;
  payout: DestinationPayoutInput;
  errorCode?:
    | "PROVISION_FAILED"
    | "REGISTER_FAILED"
    | "READINESS_FAILED"
    | "GENERIC";
  lastError?: string;
}

const isCvsDisabled = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  const code = error.response?.data?.error?.code as string | undefined;
  return code === "CVS_NOT_ENABLED" || code === "CVS_DISABLED";
};

/**
 * Lazy rail prepare for push-to-card payout:
 * register (when new) → usage-readiness → provision (no CVV).
 * When CVS is disabled, registration still happens and prepare is a no-op.
 */
export const preparePayoutRail = async (args: {
  linkId: string;
  payout: DestinationPayoutInput;
  capabilityKey: string;
  cardholderName?: string;
}): Promise<PreparePayoutRailResult> => {
  const { linkId, capabilityKey, cardholderName } = args;
  let payout = args.payout;

  if (payout.source !== "saved" || !payout.paymentMethodId) {
    try {
      const registered = await registerPayoutCard(
        linkId,
        payout,
        cardholderName
      );
      payout = registered;
    } catch {
      throw Object.assign(new Error("REGISTER_FAILED"), {
        code: "REGISTER_FAILED" as const,
      });
    }
  }

  const paymentMethodId = payout.paymentMethodId;
  if (!paymentMethodId) {
    return {
      status: "failed",
      payout,
      errorCode: "REGISTER_FAILED",
    };
  }

  let readiness;
  try {
    readiness = await destinationCardUsageReadiness(
      linkId,
      paymentMethodId,
      { capabilityKey }
    );
  } catch (error) {
    if (isCvsDisabled(error)) {
      return { status: "ready", payout };
    }
    return {
      status: "failed",
      payout,
      errorCode: "READINESS_FAILED",
    };
  }

  if (readiness.ready) {
    return { status: "ready", payout };
  }

  try {
    const provisioned = await provisionDestinationCard(
      linkId,
      paymentMethodId,
      { capabilityKey }
    );

    if (provisioned.ready) {
      return { status: "ready", payout };
    }

    return {
      status: "failed",
      payout,
      errorCode: "PROVISION_FAILED",
      lastError: provisioned.lastError,
    };
  } catch (error) {
    if (isCvsDisabled(error)) {
      return { status: "ready", payout };
    }

    return {
      status: "failed",
      payout,
      errorCode: "PROVISION_FAILED",
    };
  }
};

/** Strip raw PAN/tokens after prepare; keep display + id for bind. */
export const sanitizePayoutAfterPrepare = (
  payout: DestinationPayoutInput
): DestinationPayoutInput => ({
  source: "saved",
  paymentMethodId: payout.paymentMethodId,
  brand: payout.brand,
  last4: payout.last4,
  cardExpiry: payout.cardExpiry,
  cardholderName: payout.cardholderName,
});
